class MStoreProductModern extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-mstore-product-form]');
    this.variantIdInput = this.querySelector('[data-mstore-variant-id]');
    this.price = this.querySelector('[data-mstore-price]');
    this.comparePrice = this.querySelector('[data-mstore-compare-price]');
    this.addButton = this.querySelector('[data-mstore-add-button]');
    this.addButtonLabel = this.querySelector('[data-mstore-add-button-label]');
    this.availability = this.querySelector('[data-mstore-availability]');
    this.dynamicCheckout = this.querySelector('[data-mstore-dynamic-checkout]');
    this.preorderProperty = this.querySelector('[data-mstore-preorder-property]');
    this.preorderEtaProperty = this.querySelector('[data-mstore-preorder-eta-property]');
    this.variantJson = this.querySelector('[data-mstore-variant-json]');
    try { this.variants = this.variantJson ? JSON.parse(this.variantJson.textContent) : []; } catch (error) { this.variants = []; console.error('mStore product variants could not be parsed.', error); }
    this.optionInputs = Array.from(this.querySelectorAll('[data-mstore-option-input]'));
    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.handleOptionChange()));
    this.querySelectorAll('[data-mstore-media-target]').forEach((button) => button.addEventListener('click', () => this.activateMedia(button.getAttribute('data-mstore-media-target'))));
    if (this.form) this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.setupStickyBar();
    this.setupGallerySwipe();
    this.setupSizeGuide();
    this.querySelectorAll('[data-mstore-addon-add]').forEach((button) => button.addEventListener('click', () => this.addAddon(button)));
    this.classList.add('mstore-modern-ready');
    this.keepFirstPhoto = this.dataset.mstoreKeepFirstPhoto === 'true';
    this.initializing = true;
    this.handleOptionChange({ replaceHistory: false });
    this.initializing = false;
  }
  disconnectedCallback() {
    if (this.stickyObserver) this.stickyObserver.disconnect();
    document.body.classList.remove('ms-sticky-atc-on');
  }
  // Mobile sticky add-to-cart: shown once the main button has scrolled up out of view, so the shopper
  // has already passed the size picker. The CSS only displays it on phones; the body class it sets
  // reserves room at the bottom of the page so the bar never covers the footer.
  setupStickyBar() {
    this.sticky = this.querySelector('[data-mstore-sticky-atc]');
    if (!this.sticky || !this.addButton || !('IntersectionObserver' in window)) return;
    this.stickyButton = this.sticky.querySelector('[data-mstore-sticky-button]');
    this.stickyVariant = this.sticky.querySelector('[data-mstore-sticky-variant]');
    this.stickyPrice = this.sticky.querySelector('[data-mstore-sticky-price]');
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      this.setStickyVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    });
    this.stickyObserver.observe(this.addButton);
    const options = this.sticky.querySelector('[data-mstore-sticky-options]');
    if (options) options.addEventListener('click', () => {
      const target = this.querySelector('.mstore-pdp__option') || this.form;
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const checked = target.querySelector('input:checked');
      if (checked) checked.focus({ preventScroll: true });
    });
  }
  setStickyVisible(visible) {
    if (!this.sticky) return;
    this.sticky.classList.toggle('is-visible', visible);
    this.sticky.inert = !visible;
    document.body.classList.toggle('ms-sticky-atc-on', visible);
  }
  updateStickyVariant(variant) {
    if (!this.sticky) return;
    if (this.stickyPrice) this.stickyPrice.textContent = variant ? (variant.price_formatted || '') : '';
    if (this.stickyVariant) {
      const options = variant && Array.isArray(variant.options) ? variant.options.filter((value) => value && !/^default title$/i.test(value)) : [];
      this.stickyVariant.textContent = options.join(' / ');
    }
  }
  // Jibbitz add-on: one tap adds the charm without leaving the shoe. The drawer stays closed - the
  // shopper is still choosing a pair - and MStoreCart.refresh() keeps the header count and the drawer
  // contents current for when they open it.
  async addAddon(button) {
    if (button.disabled || !window.fetch) return;
    const status = this.querySelector('[data-mstore-addon-status]');
    const title = button.dataset.title || 'Item';
    const id = Number(button.dataset.variantId);
    button.disabled = true;
    button.textContent = 'Adding…';
    const root = window.Shopify?.routes?.root || '/';
    const addLine = async () => {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      return { cart: await fetch(`${root}cart.js`).then((r) => r.json()) };
    };
    try {
      const standard = window.MStoreStandardEvents;
      if (standard) await standard.cartLinesUpdate(this, { action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity: 1 }], run: addLine });
      else await addLine();
      if (window.MStoreCart) await window.MStoreCart.refresh();
      button.textContent = 'Added ✓';
      button.classList.add('is-added');
      if (status) status.textContent = `${title} added to your cart.`;
      setTimeout(() => { button.textContent = 'Add'; button.classList.remove('is-added'); button.disabled = false; }, 2200);
    } catch (error) {
      console.error(error);
      button.textContent = 'Try again';
      button.disabled = false;
      if (status) status.textContent = `${title} could not be added. Please try again.`;
    }
  }
  // Phones: the media stage is a horizontal scroll-snap strip (see the CSS), so a swipe moves between photos.
  // The scroll position drives the active thumbnail and the counter; a thumbnail tap or a colour change
  // scrolls the strip. On desktop the stage is not scrollable and activateMedia only toggles panels.
  setupGallerySwipe() {
    this.stage = this.querySelector('.mstore-pdp__media-stage');
    this.mediaCount = this.querySelector('[data-mstore-media-count]');
    if (!this.stage) return;
    this.panels = Array.from(this.stage.querySelectorAll('[data-mstore-media-id]'));
    if (this.panels.length < 2) return;
    let frame = 0;
    this.stage.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!this.isStageScrollable()) return;
        const panel = this.panels[Math.round(this.stage.scrollLeft / this.stage.clientWidth)];
        if (panel) this.activateMedia(panel.getAttribute('data-mstore-media-id'), { fromScroll: true });
      });
    }, { passive: true });
    const active = this.stage.querySelector('.mstore-pdp__media-panel.is-active');
    if (active && this.isStageScrollable()) this.stage.scrollLeft = active.offsetLeft;
    this.updateMediaCount();
  }
  isStageScrollable() { return Boolean(this.stage) && this.stage.scrollWidth > this.stage.clientWidth + 1; }
  updateMediaCount() {
    if (!this.mediaCount || !this.panels) return;
    const index = this.panels.findIndex((panel) => panel.classList.contains('is-active'));
    this.mediaCount.textContent = `${Math.max(index, 0) + 1} / ${this.panels.length}`;
  }
  // Size guide dialog: opened beside the size option, with the row for the chosen size highlighted and
  // scrolled into view. ms-lock hides chat launchers and stops the page scrolling behind it, as the
  // drawer does.
  setupSizeGuide() {
    this.sizeGuide = this.querySelector('[data-mstore-size-guide]');
    if (!this.sizeGuide) return;
    const guide = this.sizeGuide;
    this.querySelectorAll('[data-mstore-size-guide-open]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      this.highlightSizeGuideRow();
      if (typeof guide.showModal === 'function') guide.showModal(); else guide.setAttribute('open', '');
      document.body.classList.add('ms-lock');
      // centre the chosen size inside the chart's own scroller, never the page
      const row = guide.querySelector('tr.is-current');
      const wrap = guide.querySelector('.mstore-size-guide__table-wrap');
      if (row && wrap) wrap.scrollTop += row.getBoundingClientRect().top - wrap.getBoundingClientRect().top - (wrap.clientHeight - row.offsetHeight) / 2;
    }));
    const close = () => { if (typeof guide.close === 'function') guide.close(); else guide.removeAttribute('open'); };
    guide.querySelectorAll('[data-mstore-size-guide-close]').forEach((button) => button.addEventListener('click', close));
    // a click on the backdrop lands on the dialog element itself
    guide.addEventListener('click', (event) => { if (event.target === guide) close(); });
    guide.addEventListener('close', () => {
      if (!document.querySelector('.ms-search-panel.is-open, .ms-cart-drawer.is-open, .ms-mobile-nav.is-open')) document.body.classList.remove('ms-lock');
    });
  }
  highlightSizeGuideRow() {
    if (!this.sizeGuide) return;
    const code = (value) => String(value || '').replace(/\(.*$/, '').replace(/\s+/g, '').toUpperCase();
    const sizeInput = this.optionInputs.find((input) => input.getAttribute('data-mstore-option-kind') === 'size' && input.checked);
    const current = sizeInput ? code(sizeInput.value) : '';
    this.sizeGuide.querySelectorAll('tr[data-size-code]').forEach((row) => {
      row.classList.toggle('is-current', current !== '' && code(row.getAttribute('data-size-code')) === current);
    });
  }
  updateIncompleteState(selectedOptions) {
    const matches = (variant) => Array.isArray(variant.options) && selectedOptions.every((value, index) => value === null || variant.options[index] === value);
    const partial = this.variants.find((variant) => matches(variant) && variant.available) || this.variants.find(matches);
    if (this.variantIdInput) this.variantIdInput.value = '';
    this.setButton('Choose a size', false);
    this.setAvailability('Choose your size above.', false);
    this.setPreorderProperties(false);
    this.toggleDynamicCheckout(false);
    if (partial) {
      if (this.price) this.price.textContent = partial.price_formatted || '';
      if (partial.featured_media_id) this.activateMedia(String(partial.featured_media_id));
    }
    if (this.stickyPrice && partial) this.stickyPrice.textContent = partial.price_formatted || '';
    if (this.stickyVariant) this.stickyVariant.textContent = selectedOptions.filter((value) => value && !/^default title$/i.test(value)).join(' / ');
  }
  // The shopper pressed the button with no size chosen: take them to the first unchosen option and say so.
  promptForMissingOption() {
    const fieldset = Array.from(this.querySelectorAll('.mstore-pdp__option')).find((group) => !group.querySelector('[data-mstore-option-input]:checked'));
    this.setAvailability('Please choose your size first.', false);
    if (this.availability) this.availability.classList.add('is-prompt');
    if (!fieldset) return;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
    fieldset.classList.remove('is-missing');
    void fieldset.offsetWidth;
    fieldset.classList.add('is-missing');
    clearTimeout(this.missingTimer);
    this.missingTimer = setTimeout(() => fieldset.classList.remove('is-missing'), 1800);
    const first = fieldset.querySelector('[data-mstore-option-input]:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }
  getSelectedOptions() {
    const groups = new Map();
    this.optionInputs.forEach((input) => { const index = Number(input.getAttribute('data-option-index')); if (!groups.has(index)) groups.set(index, []); groups.get(index).push(input); });
    return Array.from(groups.keys()).sort((a,b)=>a-b).map((index) => { const selected = groups.get(index).find((input) => input.checked); return selected ? selected.value : null; });
  }
  findVariant(options) {
    if (!this.optionInputs.length && this.variants.length === 1) return this.variants[0];
    return this.variants.find((variant) => Array.isArray(variant.options) && variant.options.length === options.length && variant.options.every((option,index)=>option===options[index]));
  }
  isPreorder(variant) { return Boolean(variant && variant.available && variant.inventory_management && variant.inventory_policy === 'continue' && Number(variant.inventory_quantity) <= 0); }
  handleOptionChange(options = {}) {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);
    this.currentVariant = variant || null;
    this.updateOptionLabels(selectedOptions);
    this.markUnavailableOptions(selectedOptions);
    // shopify:product:select - the buyer changed an option, before anything is added. The event
    // carries the options they chose and its promise resolves with the variant they map to.
    if (window.MStoreStandardEvents) {
      const names = Array.from(this.querySelectorAll('[data-mstore-option-name]')).map((el) => el.getAttribute('data-mstore-option-name'));
      const pairs = selectedOptions
        .map((value, index) => ({ name: names[index] || `Option ${index + 1}`, value: value || '' }))
        .filter((pair) => pair.value !== '');
      if (pairs.length) {
        window.MStoreStandardEvents.productSelect(this, {
          product: { id: this.dataset.mstoreProductId, title: this.dataset.mstoreProductTitle, handle: this.dataset.mstoreProductHandle },
          selectedOptions: pairs,
          variant: variant || null,
        });
      }
    }
    // An option left unchosen (the size, which the page no longer preselects) is not an unavailable
    // combination: the button asks for it, and the photo and price still follow the options that are set.
    const incomplete = !variant && selectedOptions.some((value) => value === null);
    if (incomplete) this.updateIncompleteState(selectedOptions);
    else this.updateVariantState(variant);
    if (variant && options.replaceHistory !== false && window.history?.replaceState) { const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url.toString()); }
  }
  // What the shopper can see before clicking: which choices cannot be ordered at all.
  //   size  - judged with the other selections (this colour, this size is what they are buying)
  //   other - kept reachable while any variant in that value can be ordered, so one sold-out size
  //           never locks the shopper out of a colour that has stock elsewhere
  // A choice that is merely sold out is struck through and dimmed; it is only disabled when it is not
  // the one currently selected, because the shopper still has to be able to move off it.
  markUnavailableOptions(selectedOptions) {
    const groups = new Map();
    this.optionInputs.forEach((input) => {
      const index = Number(input.getAttribute('data-option-index'));
      if (!groups.has(index)) groups.set(index, []);
      groups.get(index).push(input);
    });
    groups.forEach((inputs, index) => {
      const kind = inputs[0].getAttribute('data-mstore-option-kind') || 'other';
      inputs.forEach((input) => {
        let orderable = false;
        if (kind === 'size') {
          const candidate = selectedOptions.slice();
          candidate[index] = input.value;
          const variant = this.findVariant(candidate);
          orderable = Boolean(variant && variant.available);
        } else {
          orderable = this.variants.some((variant) => Array.isArray(variant.options) && variant.options[index] === input.value && variant.available);
        }
        const row = input.closest('.mstore-pdp__choice') || input.closest('label') || input.parentElement;
        input.disabled = !orderable && !input.checked;
        if (row) {
          row.classList.toggle('is-sold-out', !orderable);
          row.setAttribute('aria-disabled', orderable ? 'false' : 'true');
          if (orderable) row.removeAttribute('title'); else row.setAttribute('title', 'Sold out');
        }
      });
    });
  }
  updateOptionLabels(selectedOptions) { selectedOptions.forEach((value,index)=>{ const label=this.querySelector(`[data-mstore-option-value="${index}"]`); if(label) label.textContent=value||''; }); }
  updateFitLink(variant) {
    const link = this.querySelector('[data-mstore-fit-link]');
    if (!link || !variant || !Array.isArray(variant.options)) return;
    const picked = variant.options.find((value) => /^[CJMW]\d/i.test(String(value || '').trim())) || variant.options[0];
    const size = String(picked || '').trim();
    // a product with no options reports "Default Title" - nothing to preselect
    if (!size || /^default title$/i.test(size)) return;
    const url = new URL(link.getAttribute('href'), window.location.href);
    url.searchParams.set('size', size);
    link.setAttribute('href', url.href);
  }
  updateVariantState(variant) {
    this.updateStickyVariant(variant);
    if (!variant) { if(this.variantIdInput)this.variantIdInput.value=''; this.setButton('Unavailable',true); this.setAvailability('This combination is unavailable.',false); this.setPreorderProperties(false); this.toggleDynamicCheckout(false); return; }
    if(this.variantIdInput)this.variantIdInput.value=variant.id;
    this.updateFitLink(variant);
    if(this.price)this.price.textContent=variant.price_formatted||'';
    if(this.comparePrice){const show=Number(variant.compare_at_price||0)>Number(variant.price||0);this.comparePrice.textContent=show?(variant.compare_at_price_formatted||''):'';this.comparePrice.hidden=!show;}
    const preorder=this.isPreorder(variant);
    if(preorder){this.setButton('Pre-order',false);this.setAvailability('Pre-order: usually arrives in 10-14 days.',true);this.setPreorderProperties(true);this.toggleDynamicCheckout(false);}else if(variant.available){this.setButton('Add to cart',false);this.setAvailability('Ready to order.',false);this.setPreorderProperties(false);this.toggleDynamicCheckout(true);}else{this.setButton('Sold out',true);this.setAvailability('Currently unavailable.',false);this.setPreorderProperties(false);this.toggleDynamicCheckout(false);}
    if(variant.featured_media_id)this.activateMedia(String(variant.featured_media_id));this.updateSku(variant);
  }
  setButton(label,disabled){if(this.stickyButton){this.stickyButton.textContent=label;this.stickyButton.disabled=Boolean(disabled);}if(!this.addButton)return;if(this.addButtonLabel)this.addButtonLabel.textContent=label;this.addButton.disabled=Boolean(disabled);}
  setAvailability(message,preorder){if(!this.availability)return;this.availability.textContent=message;this.availability.classList.toggle('is-preorder',Boolean(preorder));this.availability.classList.remove('is-prompt');}
  updateSku(variant){const el=this.querySelector('[data-mstore-sku]');if(!el)return;const val=el.querySelector('[data-mstore-sku-value]');const sku=(variant&&variant.sku)?String(variant.sku):'';if(val)val.textContent=sku;el.classList.toggle('is-empty',!sku);}
  setPreorderProperties(preorder){if(this.preorderProperty)this.preorderProperty.value=preorder?'true':'';if(this.preorderEtaProperty)this.preorderEtaProperty.value=preorder?'10-14 days':'';}
  toggleDynamicCheckout(show){if(this.dynamicCheckout)this.dynamicCheckout.classList.toggle('is-hidden',!show);}
  activateMedia(mediaId,options={}){if(!mediaId)return;/* first pass only: the server chose the opening photo (the one on the card) and asked for it to stay */if(this.initializing&&this.keepFirstPhoto)return;const panels=this.querySelectorAll('[data-mstore-media-id]');let found=null;panels.forEach((panel)=>{const active=panel.getAttribute('data-mstore-media-id')===String(mediaId);panel.classList.toggle('is-active',active);if(active)found=panel;});if(!found)return;this.updateMediaCount();if(!options.fromScroll&&this.isStageScrollable()){const smooth=!window.matchMedia('(prefers-reduced-motion: reduce)').matches;this.stage.scrollTo({left:found.offsetLeft,behavior:smooth?'smooth':'auto'});}this.querySelectorAll('[data-mstore-media-target]').forEach((button)=>{const active=button.getAttribute('data-mstore-media-target')===String(mediaId);button.classList.toggle('is-active',active);button.setAttribute('aria-current',active?'true':'false');});}
  async handleSubmit(event){
    if(this.form&&!this.currentVariant&&this.getSelectedOptions().some((value)=>value===null)){event.preventDefault();this.promptForMissingOption();return;}
    if(!this.form||!this.currentVariant||this.addButton?.disabled||!window.fetch||!window.MStoreCart)return;
    event.preventDefault();
    const previous=this.addButtonLabel?.textContent||'Add to cart';this.setButton('Adding…',true);
    const root=window.Shopify?.routes?.root||'/';
    const addLine=async()=>{
      const response=await fetch(`${root}cart/add.js`,{method:'POST',headers:{Accept:'application/json'},body:new FormData(this.form)});
      if(!response.ok)throw new Error(`Cart add failed: ${response.status}`);
      return { cart: await fetch(`${root}cart.js`).then((r) => r.json()) };
    };
    try{
      // shopify:cart:lines-update is dispatched as the add starts and its promise settles when the
      // request finishes, so an app can show a loading state and learn the outcome without watching
      // this theme's network calls; a failure also dispatches shopify:cart:error.
      const standard=window.MStoreStandardEvents;
      if(standard){await standard.cartLinesUpdate(this,{action:'add',context:'product',lines:[{merchandiseId:this.currentVariant.id,quantity:1}],run:addLine});}
      else{await addLine();}
      document.dispatchEvent(new CustomEvent('mstore:cart-updated'));
      this.updateVariantState(this.currentVariant);
    }catch(error){console.error(error);if(this.addButtonLabel)this.addButtonLabel.textContent=previous;this.addButton.disabled=false;HTMLFormElement.prototype.submit.call(this.form);}
  }
}
if(!customElements.get('mstore-product-modern'))customElements.define('mstore-product-modern',MStoreProductModern);